import { cn } from "@faire/prototype-playground/lib/utils";
import * as React from "react";

const variantStyles: Record<string, string> = {
  default:
    "bg-[#111827] text-(--slate-color-action-text-inverse) hover:bg-[#374151]",
  destructive:
    "bg-[#ef4444] text-(--slate-color-action-text-inverse) hover:bg-[#dc2626]",
  outline:
    "border border-[#e5e7eb] bg-fs-surface-primary hover:bg-fs-surface-primary hover:text-[#111827]",
  secondary:
    "bg-(--slate-color-action-surface-inverse-hover) text-[#111827] hover:bg-[#e5e7eb]",
  ghost:
    "hover:bg-(--slate-color-action-surface-inverse-hover) hover:text-[#111827]",
  link: "text-[#111827] underline-offset-4 hover:underline",
};

const sizeStyles: Record<string, string> = {
  default: "h-9 px-4 py-2",
  sm: "h-8 rounded-md px-3 text-xs",
  lg: "h-10 rounded-md px-8",
  icon: "h-9 w-9",
};

function buttonVariants(options: {
  variant?: keyof typeof variantStyles;
  size?: keyof typeof sizeStyles;
  className?: string;
}) {
  const { variant = "default", size = "default", className } = options;
  return cn(
    "inline-flex items-center justify-center gap-2 truncate rounded-md text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))] focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
    variantStyles[variant] ?? variantStyles.default,
    sizeStyles[size] ?? sizeStyles.default,
    className
  );
}

export type ButtonVariant = keyof typeof variantStyles;
export type ButtonSize = keyof typeof sizeStyles;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={buttonVariants({ variant, size, className })}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
